import React, { useState } from 'react';
import WorkflowButton from './WorkflowButton';
import WorkflowList from './WorkflowList';
import MediaUpload from './MediaUpload';
import AddressFormModal from './AddressFormModal';
import LocationCapture from './LocationCapture';
import { Download } from 'lucide-react';
import { downloadFile } from '@/utils/download.utils';
import { WorkflowMessage as WorkflowMessageType } from '../../types/workflow.types';
import { ChatbotWidgetInitResponse } from '../../services/chatbotWidget.service';

interface WorkflowMessageProps {
  message: WorkflowMessageType;
  onButtonClick: (buttonId: string, buttonLabel?: string) => void;
  onListItemSelect: (itemId: string, itemTitle?: string) => void;
  onMediaUpload?: (file: File) => Promise<void>;
  onAddressSubmit?: (address: string) => void;
  onLocationCapture?: (latitude: number, longitude: number) => void;
  disabled?: boolean;
  className?: string;
  config?: ChatbotWidgetInitResponse;
}

const WorkflowMessage: React.FC<WorkflowMessageProps> = ({
  message,
  onButtonClick,
  onListItemSelect,
  onMediaUpload,
  onAddressSubmit,
  onLocationCapture,
  disabled = false,
  className = '',
  config,
}) => {
  const [showAddressModal, setShowAddressModal] = useState(false);
  const handleDownload = downloadFile;

  const getFilename = (url: string, type: string) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
      return filename || `media.${type.split('/')[1] || 'file'}`;
    } catch {
      return `media.${type.split('/')[1] || 'file'}`;
    }
  };

  const renderMediaContent = () => {
    if (message.mediaUrl && message.mediaType) {
      if (message.mediaType.startsWith('image/') || message.mediaType === 'image') {
        return (
          <div className="relative group px-3 py-3">
            <img
              src={message.mediaUrl}
              alt={message.caption || 'Media'}
              className="max-w-full h-auto rounded-xl block cursor-pointer hover:opacity-95 transition-opacity active:scale-[0.98]"
              style={{ objectFit: 'contain' }}
              onClick={() => handleDownload(message.mediaUrl!, getFilename(message.mediaUrl!, message.mediaType!))}
              title="Click to download"
            />
          </div>
        );
      } else if (message.mediaType.startsWith('video/') || message.mediaType === 'video') {
        return (
          <div className="relative group px-3 py-3">
            <video
              src={message.mediaUrl}
              controls
              className="max-w-full h-auto rounded-xl block cursor-pointer hover:opacity-95 transition-opacity"
              style={{ objectFit: 'contain' }}
              onClick={(e) => {
                // If the user clicks the controls, don't trigger download
                if ((e.target as HTMLElement).tagName !== 'VIDEO') return;
                handleDownload(message.mediaUrl!, getFilename(message.mediaUrl!, message.mediaType!));
              }}
              title="Click video area to download"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      } else {
        return (
          <div className="p-3 border border-gray-200 rounded-lg mb-3 hover:bg-gray-50 transition-colors">
            <a
              href={message.mediaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.preventDefault();
                handleDownload(message.mediaUrl!, getFilename(message.mediaUrl!, message.mediaType!));
              }}
            >
              <Download size={16} />
              <span>{message.caption || 'Download File'}</span>
            </a>
          </div>
        );
      }
    }
    return null;
  };

  const renderContent = () => {
    switch (message.messageType) {
      case 'button':
      case 'media_button':
        return (
          <div>
            {renderMediaContent()}
            {message.text && (
              <div className="px-3 py-3 text-gray-800 text-sm whitespace-pre-wrap">
                {message.text}
              </div>
            )}
            {message.caption && !message.text && (
              <div className="px-3 py-3 text-gray-800 text-sm whitespace-pre-wrap">
                {message.caption}
              </div>
            )}
            {message.buttons && message.buttons.length > 0 && (
              <WorkflowButton
                buttons={message.buttons}
                onButtonClick={onButtonClick}
                disabled={disabled}
              />
            )}
          </div>
        );

      case 'list':
        return (
          <div>
            {message.sections && message.sections.length > 0 && (
              <WorkflowList
                header={message.header}
                body={message.body}
                footer={message.footer}
                buttonText={message.buttonText}
                sections={message.sections}
                onItemSelect={onListItemSelect}
                disabled={disabled}
              />
            )}
          </div>
        );

      case 'question':
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 text-sm px-3 py-3">
                {message.text}
              </div>
            )}
          </div>
        );

      case 'media_question':
        return (
          <div className='px-3 py-3'>
            {message.text && (
              <div className="tex-gray-900 dark:text-gray-100 text-sm px-3 py-3">
                {message.text}
              </div>
            )}
            {onMediaUpload ? (
              <MediaUpload
                onUpload={onMediaUpload}
                disabled={disabled}
                className="mt-3"
                config={config}
              />
            ) : (
              <div className="mt-2 text-sm tex-gray-900 dark:text-gray-100 p-4 bg-gray-50 rounded-lg">
                Please upload a file to continue.
              </div>
            )}
          </div>
        );

      case 'ask_address':
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 text-sm px-3 py-3">
                {message.text}
              </div>
            )}
            <WorkflowButton
              buttons={[
                {
                  id: 'provide_address',
                  label: 'Provide Address'
                }
              ]}
              onButtonClick={(buttonId) => {
                if (buttonId === 'provide_address') {
                  setShowAddressModal(true);
                }
              }}
              disabled={disabled}
            />

            {/* Address Form Modal */}
            <AddressFormModal
              isOpen={showAddressModal}
              onClose={() => setShowAddressModal(false)}
              onSubmit={(address: any) => {
                setShowAddressModal(false);
                if (onAddressSubmit) {
                  onAddressSubmit(address);
                }
              }}
              config={config}
              prompt={message.text}
              disabled={disabled}
            />
          </div>
        );

      case 'ask_location':
        return (
          <div>
            {onLocationCapture ? (
              <LocationCapture
                onCapture={onLocationCapture}
                prompt={message.text}
                disabled={disabled}
              />
            ) : (
              <div className="text-gray-800 text-sm px-3 py-3">
                {message.text || 'Please share your location'}
              </div>
            )}
          </div>
        );

      case 'text':
      default:
        return (
          <div>
            {message.text && (
              <div className="text-gray-800 text-sm px-3 py-3">
                {message.text}
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className={`workflow-message ${className}`}>
      {message.error ? (
        <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {message.error}
        </div>
      ) : (
        renderContent()
      )}
    </div>
  );
};

export default WorkflowMessage;
